import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FileSignature, AlertCircle, CheckCircle, Download } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { LoadingState } from '../../components/shared/LoadingState';
import { PDFPreview } from './components/PDFPreview';
import { SignatureModal } from '../signatures/components/SignatureModal';
import { useDocuments } from '../../core/hooks/useDocuments';
import { Card, CardContent } from '../../components/ui/Card';
import { useToast } from '../../core/hooks/useToast';

export const DocumentSigningPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { validateDocumentToken, submitDocumentSignature } = useDocuments(null, null);

  const [documentInfo, setDocumentInfo] = useState<any>(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    // Skip validation if we already have document info (prevents infinite loops)
    // Only re-validate if token changes
    if (documentInfo && documentInfo.pdfUrl) {
      return;
    }

    if (!token) {
      setError('Invalid signing link');
      setLoadingInfo(false);
      return;
    }

    // Validate the token to get the PDF URL (works before and after signing)
    const validateToken = async () => {
      setLoadingInfo(true);
      setError(null); // Clear any previous errors

      try {
        const info = await validateDocumentToken(token);
        if (!info || !info.pdfUrl || !info.documentId) {
          setError('Invalid response from server. Please try again.');
          setLoadingInfo(false);
          return;
        }

        setDocumentInfo(info);
        setError(null);

        if (info.isTokenUsed) {
          setSigned(true);
        } else {
          setSigned(false);
        }
      } catch (err: any) {
        const errorMessage = err.message || 'Invalid or expired signing link';

        if (errorMessage.includes('expired')) {
          setError('This signing link has expired. Please contact the sender for a new link.');
        } else if (errorMessage.includes('used') || errorMessage.includes('already been used')) {
          setError('This document has already been signed. Please contact the sender for access.');
        } else {
          setError(errorMessage);
        }
      } finally {
        setLoadingInfo(false);
      }
    };

    validateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]); // Only depend on token to prevent infinite loops

  const handleSubmitSignature = async (data: {
    signatureType: 'draw' | 'type';
    signatureImage?: string;
    typedName?: string;
    signingDate: string;
    consentGiven: boolean;
  }) => {
    if (!token) return;

    setSubmitting(true);
    try {
      await submitDocumentSignature(
        token,
        data.signatureType,
        data.signatureImage,
        data.typedName,
        data.signingDate,
        data.consentGiven
      );

      setLoadingInfo(true);
      try {
        const info = await validateDocumentToken(token);
        if (info && info.pdfUrl) {
          setDocumentInfo(info);
        }
      } catch {
        // Keep existing document info when token refresh fails after signature submit.
      } finally {
        setLoadingInfo(false);
      }

      setSigned(true);
      setSignatureModalOpen(false);
    } catch (err: any) {
      toast.error(
        "Your signature wasn't submitted",
        err.message ||
          'Nothing was saved. Check your connection and try signing again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingInfo) {
    return <LoadingState message="Loading document..." />;
  }

  if (error || !documentInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <AlertCircle className="h-12 w-12 text-error mx-auto" />
              <h2 className="text-xl font-semibold">Unable to Load Document</h2>
              <p className="text-gray-600">{error || 'Document not found'}</p>
              <p className="text-sm text-gray-500">
                This link may have expired or been used already. Please contact the sender for a new link.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show PDF even after signing - just show success banner

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile-first sticky header */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-semibold">Sign Document</h1>
              <p className="text-sm text-gray-600">{documentInfo.title}</p>
            </div>
            <FileSignature className="h-6 w-6 text-primary" />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Success Message Banner - shown after signing */}
        {signed && (
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-success flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-green-900">Document Signed Successfully</h2>
                  <p className="text-sm text-green-700 mt-1">
                    Thank you for signing the document. Your signature has been recorded. You can view the document below.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Document Info */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2 mb-4">
              <h2 className="font-semibold">{signed ? 'Signed Document' : documentInfo.requiresSignature ? 'Document for Review and Signature' : 'Document'}</h2>
              <p className="text-sm text-gray-600">
                {signed
                  ? 'View your signed document below.'
                  : documentInfo.requiresSignature
                  ? 'Please review the document below. Once you are ready, click Sign Document to proceed.'
                  : 'View the document below. You can download it if needed.'}
              </p>
            </div>

            {/* PDF Viewer */}
            <div className="border rounded-lg overflow-hidden">
              <PDFPreview
                file={documentInfo.signedPdfUrl || documentInfo.pdfUrl}
                showDownload={true}
              />
            </div>

            {/* Download Button */}
            <div className="flex justify-end mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  // Force download by creating a link with download attribute
                  const link = document.createElement('a');
                  link.href = documentInfo.pdfUrl;
                  link.download = `${documentInfo.title || 'document'}.pdf`;
                  link.target = '_blank';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                <Download className="mr-2 h-4 w-4" />
                Download PDF
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Signing Action - Only show if signature is required */}
        {!signed && !documentInfo.isTokenUsed && documentInfo.requiresSignature && (
          <div className="sticky bottom-0 bg-white border-t shadow-lg p-4 rounded-t-lg md:relative md:bg-transparent md:border-0 md:shadow-none md:p-0">
            <Button
              onClick={() => setSignatureModalOpen(true)}
              className="w-full md:w-auto"
              size="lg"
            >
              <FileSignature className="mr-2 h-5 w-5" />
              Sign Document
            </Button>
            <p className="text-xs text-gray-500 mt-2 text-center md:text-left">
              By signing, you agree to the terms and conditions of this document.
            </p>
          </div>
        )}
        {!signed && documentInfo.isTokenUsed && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              This document has already been signed. You can view it above.
            </p>
          </div>
        )}
        {!signed && !documentInfo.isTokenUsed && !documentInfo.requiresSignature && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              This document does not require a signature. You can view and download it above.
            </p>
          </div>
        )}
      </div>

      {/* Signature Modal */}
      <SignatureModal
        open={signatureModalOpen}
        onClose={() => setSignatureModalOpen(false)}
        onSubmit={handleSubmitSignature}
        showOtp={false}
        loading={submitting}
      />
    </div>
  );
};
